import { ApolloClient, HttpLink } from '@apollo/client';
import { includes, isEmpty, isNull, join, memoize, pick, values } from 'lodash/fp';
import { InMemoryCache } from '@apollo/client/cache';
import { RetryLink } from '@apollo/client/link/retry';
import moment from 'moment';

import { DATASET, MIDDAY, MODE, NOW } from '../constants';
import browserUtil from '../../utils/browser';
import config from '../../config';
import constants from '../../ahm-constants';
import generateLiveFilteringApi from './utils/generate-live-filtering-api-uri';
import generateSanityUri from './utils/generate-sanity-uri';
import getIsProductionEnv from '../../utils/get-is-production-env';
import getIsServer from '../../utils/get-is-server';
import logger from '../../logging';
import unfixFpIterator from '../../../utils/unfix-fp-iterator';

const FILTERED_SUFFIX = '_filtered';

const sanityConfig = config.get('sanity');

type FetchOptions = NonNullable<Parameters<typeof fetch>[1]>;

const addAuthHeader = (options: FetchOptions, token: string): FetchOptions => ({
  ...options,
  headers: { ...(options.headers as Record<string, string>), Authorization: `Bearer ${token}` },
});

// This creates the CMS client for the client side logic and CMS interactions, with the server having a similar copy of this
// code. Any changes done to this file needs to be replicated on the server's CMS client as well (particularly the createClient
// and getUri methods). These would most probably get combined when Next.js is implemented.
// @ts-expect-error - Automatic, Please fix when editing this file
const createClient = (dataset, mode, uri, token, fetchPolicy) => {
  const isServer = getIsServer();
  const cache = new InMemoryCache();
  const isTokenRequired = token && mode === MODE.DRAFT;

  if (!isServer) {
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property '__APOLLO_STATE_CMS__' does not exist on type 'WindowType | { __productReviewCallbackQueue: { push: LodashNoop; }; __productReviewSettings: {}; activeElement: null; addEventListener: LodashNoop; ... 9 more ...; supports: LodashNoop; }'.
    cache.restore(browserUtil.getWindow()?.__APOLLO_STATE_CMS__);
  }

  const httpLink = new HttpLink({
    fetch: isTokenRequired ? (url, options) => fetch(url, addAuthHeader(options ?? {}, token)) : undefined,
    uri,
  });
  const retryLink = new RetryLink({
    attempts: (count, operation, error) => {
      if (count < 5) {
        return true;
      }
      logger
        .getLogger()
        .error('Failed multiple retries to retrieve data from CMS', { error, isServer: getIsServer(), operation });
      return false;
    },
  });

  const link = retryLink.concat(httpLink);

  const opts = {
    cache,
    clientAwareness: { name: 'sanity-sales' },
    defaultOptions: {
      query: {
        fetchPolicy,
      },
      watchQuery: {
        fetchPolicy,
      },
    },
    devtools: { enabled: config.get('NODE_ENV') !== 'production' },
    link,
    ssrMode: isServer,
  };
  return new ApolloClient(opts);
};
const memoizedCreateClient = unfixFpIterator(memoize)(createClient, (...args: unknown[]) =>
  join('|', args),
) as typeof createClient;

class Client {
  projectId = sanityConfig.projectId;

  tag = sanityConfig.tag;

  useCdn = sanityConfig.useCdn;

  useFiltered = sanityConfig.useFiltered;

  mode = MODE.PUBLISHED;

  dataset = sanityConfig.dataset;

  asAtDate = NOW;

  asAtTime = MIDDAY;

  token = null;

  liveFilteringToken = null;

  useLiveFiltering() {
    return !getIsProductionEnv() && !isNull(this.liveFilteringToken);
  }

  /**
   * @returns {ApolloClient} - Returns an ApolloClient instance, will be memoized client-side.
   */
  get() {
    const isServer = getIsServer();
    return (isServer ? createClient : memoizedCreateClient)(
      this.dataset,
      this.mode,
      this.getUri(),
      this.token,
      this.getFetchPolicy(),
    );
  }

  /**
   * @param  {string} mode - One of 'draft' or 'published'.
   */
  // @ts-expect-error - Automatic, Please fix when editing this file
  setMode(mode) {
    if (includes(mode, values(MODE))) {
      this.mode = mode;
    }
  }

  /**
   * @param  {string} dataset - One of 'staging' or 'production'.
   */
  // @ts-expect-error - Automatic, Please fix when editing this file
  setDataset(dataset) {
    if (includes(dataset, values(DATASET))) {
      this.dataset = dataset;
    }
  }

  /**
   * @param  {string} asAtDate - Date-string dd/mm/yyyy or 'NOW'.
   */
  // @ts-expect-error - Automatic, Please fix when editing this file
  setAsAtDate(asAtDate) {
    if (!isEmpty(asAtDate)) {
      this.asAtDate = asAtDate;
    }
  }

  /**
   * @param  {string} asAtTime - Time-string hh:mm.
   */
  // @ts-expect-error - Automatic, Please fix when editing this file
  setAsAtTime(asAtTime) {
    if (!isEmpty(asAtTime)) {
      this.asAtTime = asAtTime;
    }
  }

  /**
   * @param  {string} apiToken - the api token required to access draft data.
   */
  // @ts-expect-error - Automatic, Please fix when editing this file
  setApiToken(apiToken) {
    if (!isEmpty(apiToken)) {
      this.token = apiToken;
    }
  }

  /**
   * @param  {string} liveFilteringToken - the api token required to perform live filtering aka time travel.
   */
  // @ts-expect-error - Automatic, Please fix when editing this file
  setLiveFilteringToken(liveFilteringToken) {
    if (!isEmpty(liveFilteringToken)) {
      this.liveFilteringToken = liveFilteringToken;
    }
  }

  /**
   * @returns {string} - Returns an endpoint uri for the client instance, with token.
   */
  getUri() {
    if (!this.useLiveFiltering() || this.asAtDate === NOW) {
      const dataset = `${this.dataset}${this.useFiltered ? FILTERED_SUFFIX : ''}`;
      return generateSanityUri({ dataset, projectId: this.projectId, tag: this.tag, useCdn: this.useCdn });
    }

    const asAtMoment = moment(`${this.asAtDate} ${this.asAtTime}`, `${constants.DATE_FORMAT.STANDARD} HH:mm`);
    const dateTimeString = `${asAtMoment.format('YYYYMMDDTHHmmSS')}`;
    return generateLiveFilteringApi({
      ...pick(['endpoint'], sanityConfig.liveFilteringApi),
      asAtDateTime: dateTimeString,
      dataset: this.dataset,
      mode: this.mode,
      token: this.liveFilteringToken,
    });
  }

  /**
   * @returns {string} - Returns the fetchPolicy to be applied to the client.
   */
  getFetchPolicy() {
    const isServer = getIsServer();
    const isTimeTravelling = this.useLiveFiltering() && this.asAtDate !== NOW;
    const clientFetchPolicy = isTimeTravelling ? sanityConfig.fetchPolicy.timeTravel : sanityConfig.fetchPolicy.client;
    return isServer ? sanityConfig.fetchPolicy.server : clientFetchPolicy;
  }
}

const client = new Client();

export default client;
export { addAuthHeader };
